"use client"

import { BlogEntryThin } from "@/api/generated/BMWInterfaces"
import colors from "@/lib/theme/colors"
import { Button, styled } from "@mui/material"
import Image from "next/image"
import { useRouter } from "next/navigation"

const StyledCard = styled("article")<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.borderGrey};
  border-radius: 8px;
  overflow: hidden;
  background: ${colors.white};
  position: relative;
  cursor: pointer;

  ${({ compact }) =>
    compact &&
    `
    width: 235px;
    min-width: 235px;
  `}

  ${(p) => p.theme.breakpoints.up("md")} {
    max-width: 328px;
    width: 100%;
    ${({ compact }) =>
      compact &&
      `
      min-width: unset;
    `}
  }

  &:hover img.blog-card-image {
    transform: scale(1.06);
  }
`

const StyledCategory = styled("span")`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  display: flex;
  padding: 5px 7px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  border: 1px solid ${colors.directGray};
  background: rgba(242, 242, 242, 0.8);
  height: 36px;
  color: ${colors.directBlack};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  box-sizing: border-box;
`

const StyledImageWrap = styled("div")`
  position: relative;
  width: 100%;
  aspect-ratio: 326 / 159;
  background: ${colors.directDisable};
  overflow: hidden;

  img.blog-card-image {
    transition: transform 0.3s ease;
  }
`

const StyledContent = styled("div")<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ compact }) => (compact ? "16px" : "8px")};
  padding: ${({ compact }) => (compact ? "16px" : "15px")};
  border-top: 1px solid ${colors.borderGrey};
  flex-grow: 1;

  ${({ theme, compact }) =>
    compact &&
    `
    ${theme.breakpoints.up("md")} {
      gap: 8px;
      padding: 15px;
    }
  `}
`

const StyledTitle = styled("h2")<{ compact?: boolean }>`
  margin: 0;
  color: ${colors.black};
  font-size: ${({ compact }) => (compact ? "18px" : "24px")};
  font-weight: ${({ compact }) => (compact ? "500" : "400")};
  line-height: ${({ compact }) => (compact ? "normal" : "35px")};

  ${({ theme, compact }) =>
    compact &&
    `
    ${theme.breakpoints.up("md")} {
      font-size: 24px;
      font-weight: 400;
      line-height: 35px;
    }
  `}
`

const StyledSubtitle = styled("p")<{ compact?: boolean }>`
  margin: 0;
  color: ${colors.black};
  font-size: ${({ compact }) => (compact ? "16px" : "18px")};
  font-weight: 400;
  line-height: ${({ compact }) => (compact ? "normal" : "26px")};
  margin-bottom: 16px;

  ${({ theme, compact }) =>
    compact &&
    `
    ${theme.breakpoints.up("md")} {
      font-size: 18px;
      line-height: 26px;
    }
  `}
`

const StyledDate = styled("span")<{ compact?: boolean }>`
  color: ${colors.black};
  font-size: ${({ compact }) => (compact ? "14px" : "16px")};
  font-weight: 400;
  line-height: 1.5;

  ${({ theme, compact }) =>
    compact &&
    `
    ${theme.breakpoints.up("md")} {
      font-size: 16px;
    }
  `}
`

const StyledReadMore = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  gap: 4px;
  padding: 0 15px;
  color: ${colors.directBlack};
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  width: fit-content;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${colors.directYellow};
  background: ${colors.white};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${colors.directYellow};
  }
`

const formatDate = (value: string | null | undefined): string => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return ""
  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year = String(date.getFullYear()).slice(-2)
  return `${day}/${month}/${year}`
}

interface BlogCardProps {
  blog: BlogEntryThin
  category?: string
  compact?: boolean
}

const BlogCard = ({ blog, category, compact }: BlogCardProps): React.ReactElement => {
  const router = useRouter()
  const previewImage = blog.lobbyPreviewImage
  const hasImage = Boolean(previewImage?.imageUrl)

  return (
    <StyledCard compact={compact} onClick={() => router.push(`/blog/${blog.id}`)}>
      {category && <StyledCategory>{category}</StyledCategory>}
      <StyledImageWrap>
        {hasImage && (
          <Image
            src={previewImage.imageUrl}
            alt={previewImage.alt || blog.title}
            fill
            sizes="(max-width: 899px) 100vw, (max-width: 1199px) 50vw, 33vw"
            style={{ objectFit: "cover" }}
            className="blog-card-image"
          />
        )}
      </StyledImageWrap>

      <StyledContent compact={compact}>
        <StyledDate compact={compact}>{formatDate(blog.dateModified)}</StyledDate>
        <StyledTitle compact={compact}>{blog.title}</StyledTitle>
        <StyledSubtitle compact={compact}>{blog.subTitle}</StyledSubtitle>

        <StyledReadMore
          onClick={(e) => {
            e.stopPropagation()
            router.push(`/blog/${blog.id}`)
          }}
        >
          <span>לקריאה</span>
          <Image src="/assets/chevronleft.svg" alt="" width={16} height={16} aria-hidden />
        </StyledReadMore>
      </StyledContent>
    </StyledCard>
  )
}

export default BlogCard
